import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { LoadingState, LoadingStates } from 'globals/types';
import { temporaryEndpoints } from 'http/endpoints';
import { isStoreInitialized, performSetAction } from 'models/utils/utils';
import { Web, WebProps } from './web/web';

export interface RedirectsWebProps {
  data: WebProps[];
  status: LoadingState;
}

export const RedirectsWeb = types
  .model({
    data: types.optional(types.array(Web), []),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: RedirectsWebProps): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        ...props,
      });
    },
    updateStatus(status: LoadingState): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        status,
      });
    },
    setRedirects(redirectsList: WebProps[]): void {
      applySnapshot(self, {
        ...getSnapshot(self),
        data: [...redirectsList],
      });
    },
  }))
  .actions((self) => ({
    async setRedirectsStore(): Promise<void> {
      await performSetAction({
        store: self,
        endpoint: temporaryEndpoints.developers.redirectsWeb,
      });
    },
    addRedirect(): void {
      const snapData = getSnapshot(self.data);
      self.setRedirects([
        ...snapData,
        { id: snapData[snapData.length - 1].id + 1, origin: '', comment: '' },
      ]);
    },
    removeRedirect(selectedId: number): void {
      const snapData = getSnapshot(self.data);
      self.setRedirects(snapData.filter(({ id }) => id !== selectedId));
    },
    updateRedirect(changedField: WebProps): void {
      const snapData = getSnapshot(self.data);
      self.setRedirects([
        ...snapData.map((field) =>
          field.id === changedField.id ? changedField : field
        ),
      ]);
    },
  }))
  .actions((self) => ({
    async init(): Promise<void> {
      const { status } = getSnapshot(self);
      if (isStoreInitialized(status)) return;
      await self.setRedirectsStore();
    },
    async update(): Promise<void> {
      await self.setRedirectsStore();
    },
  }))
  .views((self) => ({
    getRedirects(): WebProps[] {
      const { data } = self;
      return getSnapshot(data);
    },
  }));

export const redirectsWeb = RedirectsWeb.create();
export type RedirectsWebSnapshot = SnapshotIn<typeof RedirectsWeb>;
export type RedirectsWebInstance = Instance<typeof RedirectsWeb>;
export default redirectsWeb;
